import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, MapPin } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { fileUrl } from "@/components/ImageUpload";
import RatingForm, { EligibilityList } from "@/components/ratings/RatingForm";
import api, { formatApiError } from "@/lib/api";

export default function RatePlayer() {
  const { token, userId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    const req = token
      ? api.get(`/ratings/link/${token}`).then(({ data }) => ({ player: data.player, el: data.eligibility, link: data.link }))
      : Promise.all([api.get(`/ratings/profile/${userId}`), api.get(`/ratings/eligibility/${userId}`)]).then(([p, e]) => ({ player: p.data.player, el: e.data }));
    req.then(setD).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, [token, userId]);

  const pl = d?.player;
  const photo = pl?.photo ? fileUrl(pl.photo) : pl?.picture;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Rate Player" />
      <div className="max-w-2xl mx-auto px-4 py-10">
        {err ? <p className="text-center text-sm py-20" style={{ color: "#ef4444" }} data-testid="rate-error">{err}</p>
          : !d ? <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>
          : (
            <div className="rounded-3xl glow-border overflow-hidden" style={{ background: "var(--surface)" }} data-testid="rate-player-page">
              <div className="p-6 flex items-center gap-4 border-b" style={{ borderColor: "var(--border)" }}>
                {photo ? <img src={photo} alt="" className="w-16 h-16 rounded-2xl object-cover glow-border" />
                  : <div className="w-16 h-16 rounded-2xl glass flex items-center justify-center font-display font-black text-xl text-primary-c">{(pl.name || "?")[0]}</div>}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--text-2)" }}>You're rating</div>
                  <h1 className="font-display font-black text-2xl flex items-center gap-2" data-testid="rate-player-name">{pl.name}<VerifiedBadge verified={pl.verified} size={20} /></h1>
                  <div className="text-xs mono flex items-center gap-2" style={{ color: "var(--text-2)" }}>{pl.player_code}{pl.location && <><MapPin className="w-3 h-3" />{pl.location}</>}</div>
                </div>
              </div>
              <div className="p-6">
                {done ? (
                  <div className="text-center py-10" data-testid="rating-success">
                    <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: "#22c55e" }} />
                    <h2 className="font-display font-black text-2xl mt-4">{done.updated ? "Rating Updated Successfully" : "Rating Submitted Successfully"}</h2>
                    <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{done.updated ? "Your updated rating now counts; the previous version is kept in the history." : "The rating has been recorded."} {done.category === "organizer" ? "As an organizer you can update it anytime." : "You can update it again after 7 days."}</p>
                    <div className="flex justify-center gap-2 mt-6 flex-wrap">
                      <button data-testid="view-rated-profile-btn" onClick={() => nav(`/players/${pl.user_id}`)} className="rounded-full glass px-5 py-2.5 text-sm font-bold">View their profile</button>
                      <button data-testid="request-my-ratings-btn" onClick={() => nav("/players/me")} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold">Request ratings for myself</button>
                    </div>
                  </div>
                ) : d.el.eligible ? (
                  <RatingForm playerId={pl.user_id} token={token} el={d.el} onDone={setDone} />
                ) : (
                  <div data-testid="rate-not-eligible">
                    <p className="text-sm font-bold mb-4" style={{ color: "#ef4444" }}>You can't submit a rating yet:</p>
                    <EligibilityList el={d.el} />
                    <button data-testid="go-profile-btn" onClick={() => nav("/profile")} className="mt-6 rounded-full glass px-5 py-2.5 text-sm font-bold">Open my profile settings</button>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
